import { Injectable } from '@angular/core';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { AssetStoragePermissionGroupDTO, AssetStorageSettingClient, CreateAssetStoragePermissionGroupDTO, FilterDTO, SortDirection, UpdateAssetStoragePermissionGroupDTO } from 'src/app/base/models/models.swagger.generated';
import { ToastService } from 'src/app/base/services/toast.service';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { IPagedItems } from 'src/app/base/shared/syncfusion/paged-items.interface';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ErrorType } from 'src/app/core/routing/error-types';

@Injectable()
export class AssetSettingsService extends ShowInactiveService implements IAdaptorService {

  constructor(
    private assetSettingsClient: AssetStorageSettingClient,
    private toastService: ToastService  ) {
    super();
  }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    if (!sortBy) {
      sortBy = 'Name';
    }
    return this.assetSettingsClient.searchAssetStoragePermissionGroups(skip, take, sortBy, sortDirection, false, filters);
  }

  public addRecord(data: CreateAssetStoragePermissionGroupDTO): Observable<AssetStoragePermissionGroupDTO> {
    return this.assetSettingsClient.createAssetStoragePermissionGroup(data)
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.assets.asset-storage-settings.messages.create.success-title',
            'i18n.domain.assets.asset-storage-settings.messages.create.success-message');
        }),
        catchError((error: any) => {
          if (error.status === 409) {
            this.toastService.showError(
              'i18n.domain.assets.asset-storage-settings.messages.create.duplicate-title',
              'i18n.domain.assets.asset-storage-settings.messages.create.duplicate-message');
          } else {
            if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
              this.toastService.showError('i18n.domain.assets.asset-storage-settings.messages.create.fail-title', 'i18n.domain.assets.asset-storage-settings.messages.create.fail-message', null, ErrorType.serverError);
            }
          }
          return throwError(() => error);
        })
      );
  }

  public updateRecord(dto: UpdateAssetStoragePermissionGroupDTO): Observable<AssetStoragePermissionGroupDTO> {
    return this.assetSettingsClient.updateAssetStoragePermissionGroup(dto)
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.assets.asset-storage-settings.messages.update.success-title',
            'i18n.domain.assets.asset-storage-settings.messages.update.success-message');
        }),
        catchError((error: any) => {
          if (error.status === 409) {
            this.toastService.showError(
              'i18n.domain.assets.asset-storage-settings.messages.update.duplicate-title',
              'i18n.domain.assets.asset-storage-settings.messages.update.duplicate-message');
          } else {
            if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
              this.toastService.showError('i18n.domain.assets.asset-storage-settings.messages.update.fail-title', 'i18n.domain.assets.asset-storage-settings.messages.update.fail-message', null, ErrorType.serverError);
            }
          }
          return throwError(() => error);
        })
      );
  }

  public deleteRecord(id: number): Observable<void> {
    return this.assetSettingsClient.deleteAssetStoragePermissionGroup(id)
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.assets.asset-storage-settings.messages.delete.success-title',
            'i18n.domain.assets.asset-storage-settings.messages.delete.success-message');
        }),
        catchError((error: any) => {
          if(error.status === 403) {
            this.toastService.showError(
              'i18n.domain.assets.asset-storage-settings.messages.delete.forbidden-title',
              'i18n.domain.assets.asset-storage-settings.messages.delete.forbidden-message');
          } else
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.domain.assets.asset-storage-settings.messages.delete.fail-title', 'i18n.domain.assets.asset-storage-settings.messages.delete.fail-message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public getAllPermissionGroups(): Observable<AssetStoragePermissionGroupDTO[]> {
    return this.getData(0, 1000, [], "Name", SortDirection.Ascending).pipe(
      map((pagedItems: IPagedItems) => {
        return pagedItems.items as AssetStoragePermissionGroupDTO[];
      })
    );
  }
}
